import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { store } from '@reduxStore';
import {
	DriverRoute,
	GeocodeAddressRequest,
	GeocodedAddress,
	Position,
	RouteDirectionsRequest,
	transformGeocodingResponse,
	transformRouteDirectionsResponse
} from './types';

const rawBaseQuery = fetchBaseQuery({ baseUrl: 'https://atlas.microsoft.com' });

/**
 * Attaches the tenant's Azure Maps subscription key and the API version to every request.
 * The key is read per request because appSettings is loaded after the store is built.
 */
const azureMapsBaseQuery: BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError> = (args, api, extraOptions) => {
	const subscriptionKey = store.getState().appSettings?.azure.mapsSubscriptionKey;

	if (!subscriptionKey) {
		return { error: { status: 'CUSTOM_ERROR', error: 'Azure Maps subscription key is not configured.' } };
	}

	return rawBaseQuery(
		{
			...args,
			params: { ...args.params, 'api-version': '2025-01-01', 'subscription-key': subscriptionKey }
		},
		api,
		extraOptions
	);
};

function waypoint(position: Position, pointIndex: number) {
	return {
		type: 'Feature',
		geometry: { type: 'Point', coordinates: position },
		properties: { pointIndex, pointType: 'waypoint' }
	};
}

export const azureMapsApi = createApi({
	reducerPath: 'azureMapsApi',
	baseQuery: azureMapsBaseQuery,
	endpoints: (builder) => ({
		geocodeAddress: builder.query<GeocodedAddress | null, GeocodeAddressRequest>({
			query: (address) => ({
				url: '/geocode',
				params: { ...address, top: 1 }
			}),
			transformResponse: transformGeocodingResponse
		}),

		fetchRouteDirections: builder.query<DriverRoute | null, RouteDirectionsRequest>({
			query: ({ origin, destination, loadType }) => ({
				url: '/route/directions',
				method: 'POST',
				body: {
					type: 'FeatureCollection',
					features: [waypoint(origin, 0), waypoint(destination, 1)],
					travelMode: 'truck',
					vehicleSpec: { isVehicleCommercial: true, loadType },
					routeOutputOptions: ['routePath']
				}
			}),
			transformResponse: transformRouteDirectionsResponse
		})
	})
});

export const { useGeocodeAddressQuery, useFetchRouteDirectionsQuery } = azureMapsApi;
